'use client';

import { useState, useEffect, useRef, useCallback, useId, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { Search, MapPin, Users, Route, ArrowRight, BookOpen, FileText, Zap } from 'lucide-react';
import { Input , Button } from '@/components/ui';
import { useAuth } from '@/lib/useAuth';
import { getAdminSearchRoutes, getAdminRouteDestination } from '@/lib/admin/admin-route-registry';
import { useCreateActions, type CreateAction } from '@/components/dashboard/useCreateActions';

type SearchResult = {
    id: string;
    type: 'action' | 'property' | 'crew' | 'route' | 'page' | 'knowledge' | 'customer' | 'invoice';
    title: string;
    subtitle?: string;
    href: string;
    rank?: number;
    createAction?: CreateAction;
};

const QUICK_ACTIONS: SearchResult[] = [
    { id: 'dispatch-board', type: 'action', title: 'Go to Dispatch Board', subtitle: 'Monitor routes and crew progression', href: '/dispatch' },
    { id: 'fleet-status', type: 'action', title: 'View Fleet Status', subtitle: 'Check vehicle availability and Bouncie data', href: '/fleet' },
    { id: 'service-center', type: 'action', title: 'Open Service Center', subtitle: 'Manage job queue and billing lifecycle', href: '/service-center' },
    { id: 'level-pay-billing', type: 'action', title: 'Level-Pay Billing', subtitle: 'Review monthly-billed accounts and settlement balances', href: '/money?tab=level-pay' },
    { id: 'admin-pricing', type: 'action', title: 'System Pricing Settings', subtitle: 'Adjust global production rates', href: '/admin/pricing' },
];

const TYPE_ICONS: Record<string, any> = {
    action: Zap,
    property: MapPin,
    crew: Users,
    customer: Users,
    route: Route,
    page: ArrowRight,
    knowledge: BookOpen,
    invoice: FileText,
};

const TYPE_COLORS: Record<string, string> = {
    action: 'text-amber-400',
    property: 'text-emerald-400',
    crew: 'text-blue-400',
    customer: 'text-cyan-400',
    route: 'text-amber-400',
    page: 'text-zinc-400',
    knowledge: 'text-blue-400',
    invoice: 'text-amber-400',
};

export function CommandPaletteV2() {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const router = useRouter();
    const { user } = useAuth();
    const { actions, create, invoiceDialog } = useCreateActions();
    const quickActions = useMemo<SearchResult[]>(() => [
        ...actions.map(action => ({ ...action, type: 'action' as const, href: action.href || '', createAction: action })),
        ...(user?.role === 'admin' ? QUICK_ACTIONS : []),
    ], [actions, user?.role]);
    const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
    const titleId = useId();
    const descriptionId = useId();

    // Listen for Ctrl+K / Cmd+K
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setOpen(prev => !prev);
            }
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, []);

    // Focus input and reset state on open
    useEffect(() => {
        if (open) {
            inputRef.current?.focus();
            setQuery('');
            setResults([]);
            setSelectedIndex(-1);
        }
    }, [open]);

    const doSearch = useCallback(async (q: string) => {
        if (!q.trim()) {
            setResults([]);
            return;
        }

        const actionResults = quickActions.filter(a =>
            a.title.toLowerCase().includes(q.toLowerCase()) || 
            a.subtitle?.toLowerCase().includes(q.toLowerCase())
        );
        const adminResults: SearchResult[] = getAdminSearchRoutes(q)
            .filter((route) => route.audience === 'authenticated' || user?.role === 'admin')
            .flatMap((route) => {
                const href = getAdminRouteDestination(route);
                return href ? [{
                    id: `admin-${route.id}`,
                    type: 'page' as const,
                    title: route.label,
                    subtitle: route.description,
                    href,
                }] : [];
            });

        setLoading(true);
        try {
            const res = await fetch(`/api/search?q=${encodeURIComponent(q)}&limit=8`);
            if (res.ok) {
                const data = await res.json();
                const apiResults: SearchResult[] = (data.results || []).flatMap((r: {
                    id?: string;
                    entity_id?: string;
                    entity_type?: string;
                    title?: string;
                    name?: string;
                    content?: string;
                    description?: string;
                    rank?: number;
                }) => {
                    const id = r.id || r.entity_id;
                    if (!id) return [];
                    const destination = {
                        property: { type: 'property' as const, href: `/properties/${encodeURIComponent(id)}` },
                        customer: { type: 'customer' as const, href: `/customers/${encodeURIComponent(id)}` },
                        crew: { type: 'crew' as const, href: `/crews/${encodeURIComponent(id)}` },
                        invoice: { type: 'invoice' as const, href: `/billing/invoices/${encodeURIComponent(id)}` },
                        knowledge: { type: 'knowledge' as const, href: `/knowledge/${encodeURIComponent(id)}` },
                        route: { type: 'route' as const, href: `/routes/${encodeURIComponent(id)}` },
                    }[r.entity_type || ''];
                    return destination ? [{
                        id,
                        ...destination,
                        title: r.title || r.name || id,
                        subtitle: r.content || r.description,
                        rank: r.rank,
                    }] : [];
                });
                setResults([...actionResults, ...adminResults, ...apiResults]);
            } else {
                setResults([...actionResults, ...adminResults]);
            }
        } catch (e) {
            console.error('Palette search error:', e);
            setResults([...actionResults, ...adminResults]);
        }
        setLoading(false);
    }, [quickActions, user?.role]);

    function handleQueryChange(value: string) {
        setQuery(value);
        setSelectedIndex(value.trim() ? 0 : -1);
        setResults([]);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => doSearch(value), 150);
    }

    function navigate(result: SearchResult) {
        if (result.createAction) create(result.createAction);
        else router.push(result.href);
        setOpen(false);
    }

    function handleKeyDown(e: React.KeyboardEvent) {
        const items = displayResults;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, items.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && items[selectedIndex]) {
            e.preventDefault();
            navigate(items[selectedIndex]);
        }
    }

    const suggestedAdminResults: SearchResult[] = getAdminSearchRoutes()
        .filter((route) => route.visibility.suggested)
        .filter((route) => route.audience === 'authenticated' || user?.role === 'admin')
        .flatMap((route) => {
            const href = getAdminRouteDestination(route);
            return href ? [{
                id: `admin-${route.id}`,
                type: 'page' as const,
                title: route.label,
                subtitle: route.description,
                href,
            }] : [];
        });
    const displayResults = query.trim()
        ? results.filter(result => result.type !== 'action' || quickActions.some(action => action.id === result.id))
        : [...quickActions, ...suggestedAdminResults];

    return (
        <>
        {invoiceDialog}
        {open && <div className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh] px-4">
            <div className="absolute inset-0 bg-space-950/80 backdrop-blur-md" onClick={() => setOpen(false)} />
            
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
                className="relative w-full max-w-2xl bg-zinc-900 border border-white/10 rounded-3xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.8)] overflow-hidden animate-in zoom-in-95 duration-200"
            >
                <div className="sr-only">
                    <h2 id={titleId}>Global command palette</h2>
                    <p id={descriptionId}>Search Mission Control pages, entities, and quick actions.</p>
                </div>
                {/* Search Header */}
                <div className="flex items-center gap-4 px-6 py-5 border-b border-white/5">
                    <div className={`p-2 rounded-xl bg-blue-600/10 ${loading ? 'animate-pulse' : ''}`}>
                        <Search className="w-5 h-5 text-blue-400" />
                    </div>
                    <Input
                        ref={inputRef}
                        value={query}
                        onChange={e => handleQueryChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        aria-label="Search Mission Control"
                        placeholder="Search pages, actions, and available records…"
                    />
                    <div className="flex items-center gap-2">
                        <kbd className="hidden sm:block px-2 py-1 bg-zinc-800 border border-white/5 rounded-lg text-[10px] font-black text-zinc-500 font-mono shadow-inner uppercase tracking-tighter">ESC</kbd>
                    </div>
                </div>

                {/* Results Area */}
                <div className="max-h-[60vh] overflow-y-auto no-scrollbar py-3 px-3">
                    {!query && (
                        <div className="px-4 py-2 mb-1">
                            <span className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.2em]">Suggested Actions</span>
                        </div>
                    )}
                    
                    {query && results.length === 0 && !loading && (
                        <div className="px-4 py-12 text-center">
                            <div className="w-12 h-12 bg-zinc-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Search className="w-6 h-6 text-zinc-600" />
                            </div>
                            <p className="text-zinc-400 font-medium">No matches found for &ldquo;{query}&rdquo;</p>
                            <p className="text-zinc-600 text-xs mt-1">Try searching by name, address, or phone number.</p>
                        </div>
                    )}

                    <div className="space-y-1">
                        {displayResults.map((result, i) => {
                            const Icon = TYPE_ICONS[result.type] || Search;
                            const isActive = i === selectedIndex;
                            const isTopMatch = Boolean(query) && i === 0 && result.type !== 'action' && result.type !== 'page';
                            
                            return (
                                <Button
                                    key={`${result.type}-${result.id}`}
                                    onClick={() => navigate(result)}
                                    onMouseEnter={() => setSelectedIndex(i)}
                                    className={`w-full flex items-center gap-4 px-4 py-3.5 text-left rounded-2xl transition-colors duration-150 ${
                                        isActive 
                                            ? 'bg-blue-600/10 border border-blue-500/20 shadow-lg shadow-blue-500/5' 
                                            : 'bg-transparent border border-transparent hover:bg-white/[0.02]'
                                    }`}
                                >
                                    <div className={`p-2.5 rounded-xl transition-colors ${
                                        isActive ? 'bg-blue-600/20 shadow-inner' : 'bg-zinc-800/50'
                                    }`}>
                                        <Icon className={`w-5 h-5 ${isActive ? TYPE_COLORS[result.type] : 'text-zinc-500'}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className={`text-sm font-bold truncate ${isActive ? 'text-white' : 'text-zinc-300'}`}>
                                                {result.title}
                                            </p>
                                            {isTopMatch && (
                                                <span className="text-[10px] font-black uppercase tracking-tighter px-1.5 py-0.5 rounded border bg-emerald-500/10 border-emerald-500/20 text-emerald-300">
                                                    Best match
                                                </span>
                                            )}
                                            <span className={`text-[9px] font-black uppercase tracking-tighter px-1.5 py-0.5 rounded border transition-colors ${
                                                isActive 
                                                    ? 'bg-blue-500/20 border-blue-500/30 text-blue-300' 
                                                    : 'bg-zinc-800 border-white/5 text-zinc-500'
                                            }`}>
                                                {result.type}
                                            </span>
                                        </div>
                                        {result.subtitle && (
                                            <p className={`text-xs truncate mt-0.5 ${isActive ? 'text-blue-300/60' : 'text-zinc-500'}`}>
                                                {result.subtitle}
                                            </p>
                                        )}
                                    </div>
                                    {isActive && (
                                        <div className="animate-in fade-in slide-in-from-left-2">
                                            <ArrowRight className="w-4 h-4 text-blue-400 opacity-50" />
                                        </div>
                                    )}
                                </Button>
                            );
                        })}
                    </div>
                </div>

                {/* Palette Footer */}
                <div className="px-6 py-3 bg-black/20 border-t border-white/5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                            <kbd className="px-1.5 py-0.5 bg-zinc-800 border border-white/5 rounded text-[10px] font-mono text-zinc-400 shadow-inner">↑↓</kbd>
                            <span className="text-[10px] text-zinc-600 font-bold uppercase tracking-wider">Navigate</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <kbd className="px-1.5 py-0.5 bg-zinc-800 border border-white/5 rounded text-[10px] font-mono text-zinc-400 shadow-inner">ENTER</kbd>
                            <span className="text-[10px] text-zinc-600 font-bold uppercase tracking-wider">Select</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Zap className="w-3 h-3 text-amber-500/50" />
                        <span className="text-[9px] text-zinc-600 font-black uppercase tracking-widest">Power-User Mode Active</span>
                    </div>
                </div>
            </div>
        </div>}
        </>
    );
}
